/* eslint-disable indent */
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  LinkOutlined
} from "@ant-design/icons";
import { Modal, Descriptions, Button, Typography, Space, Tag, message } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import TranscriptPreview from "../TranscriptPreview";

const { Text, Link } = Typography;

const LectureTranscriptIngestionDetailsModal = ({
  open,
  onCancel,
  lecture,
  onReingest,
  loading = false,
  lecturesApi = ""
}) => {
  const [transcriptChunks, setTranscriptChunks] = useState([]);
  const [fetchingTranscript, setFetchingTranscript] = useState(false);

  const { aiDoubtSupportMetadata = {}, uniquePublicId } = lecture || {};
  const {
    transcriptFileUrl,
    transcriptIngestedAt,
    lectureContentSummary,
    ingestionError,
    ingestionStatus
  } = aiDoubtSupportMetadata;

  useEffect(() => {
    if (open && lecturesApi && uniquePublicId) {
      setFetchingTranscript(true);
      axios
        .post(`${lecturesApi}/get-transcript-preview`, {
          uniquePublicId
        })
        .then((response) => {
          const chunks = Array.isArray(response.data)
            ? response.data
            : response.data?.preview || [];
          setTranscriptChunks(chunks);
        })
        .catch((_err) => {
          message.error("Failed to load transcript preview.");
        })
        .finally(() => {
          setFetchingTranscript(false);
        });
    } else {
      setTranscriptChunks([]);
    }
  }, [open, uniquePublicId, lecturesApi]);

  let status = "NOT APPLICABLE";
  if (transcriptFileUrl) {
    status = (ingestionStatus || "PENDING").toUpperCase();
  }

  return (
    <Modal
      open={open}
      title="Transcript Ingestion Details"
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
        <Button
          key="reingest"
          type="primary"
          icon={<SyncOutlined />}
          loading={loading}
          onClick={() => onReingest(lecture)}
          disabled={!transcriptFileUrl}
        >
          Re-initiate Ingestion
        </Button>
      ]}
      width={700}
    >
      <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Status">
          {status === "COMPLETED" && (
            <Tag icon={<CheckCircleOutlined />} color="success">
              COMPLETED
            </Tag>
          )}
          {status === "FAILED" && (
            <Tag icon={<ExclamationCircleOutlined />} color="error">
              FAILED
            </Tag>
          )}
          {status === "PENDING" && (
            <Tag icon={<ClockCircleOutlined />} color="warning">
              PENDING
            </Tag>
          )}
          {status !== "COMPLETED" &&
            status !== "FAILED" &&
            status !== "PENDING" &&
            status !== "NOT APPLICABLE" && (
              <Tag icon={<SyncOutlined spin />} color="processing">
                {status}
              </Tag>
            )}
          {status === "NOT APPLICABLE" && <Tag color="default">NOT APPLICABLE</Tag>}
        </Descriptions.Item>

        {transcriptIngestedAt && (
          <Descriptions.Item label="Ingested At">
            {new Date(transcriptIngestedAt).toLocaleString()}
          </Descriptions.Item>
        )}

        {ingestionError && (
          <Descriptions.Item label="Ingestion Error">
            <Text type="danger">{ingestionError}</Text>
          </Descriptions.Item>
        )}

        {transcriptFileUrl && (
          <Descriptions.Item label="Transcript URL">
            <Link href={transcriptFileUrl} target="_blank">
              <Space>
                <Tag color="blue" icon={<LinkOutlined />}>
                  View VTT File
                </Tag>
              </Space>
            </Link>
          </Descriptions.Item>
        )}
      </Descriptions>

      <TranscriptPreview
        chunks={transcriptChunks}
        summary={lectureContentSummary}
        loading={fetchingTranscript}
        title="Parsed Transcript Preview"
        initialExpanded={false}
      />
    </Modal>
  );
};

export default LectureTranscriptIngestionDetailsModal;
